import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTable } from "react-table";
import { truncateString } from "../../../../helpers";
import primaryComponents from "../../../primaryComponents";
import CopyToClipboard from "react-copy-to-clipboard";
import Icon from "../../../../assets/icons";
import { ToastContext } from "../../../../hooks/context";
import { pathConstants } from "../../../../routes/pathContants";
import secondaryComponents from "../..";
import { MoreActions } from "./MoreAction";
import { useDeleteStorefront } from "../../../../hooks/api/mutations/useStorefront";

export const useStorefrontTable = ({ storefrontList = [] }) => {
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);
  const [isMobile, setIsMobile] = useState(false);
  const [isFoodMenuWidget, setIsFoodMenuWidget] = useState(null);
  const [isDeleteFoodMenu, setIsDeleteFoodMenu] = useState(null);

  const onCopy = () => triggerToast("Copied", "success");

  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      return (
        /android|iphone|ipad|ipod/.test(userAgent) || window.innerWidth <= 768
      );
    };
    setIsMobile(checkIfMobile());
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Storefront",
        accessor: "name",
        Cell: ({ cell: { value } }) => <span>{truncateString(value, 50)}</span>,
      },
      {
        Header: "Store",
        accessor: "store",
        Cell: ({ cell: { value } }) => <span>{value.name}</span>,
      },
      {
        Header: "URL",
        accessor: "slug_url",
        Cell: ({ cell: { value, row } }) => (
          <div className={"d-flex align-items-center"} style={{ gap: "15px" }}>
            {row?.original?.template_in_use === "template-2" ? (
              <>
                <a
                  href={`${window.location.origin}/storefront/${
                    value?.split("/storefront/")[1]
                  }`}
                  className="gray--text"
                  target="_blank"
                  rel="noreferrer"
                >
                  {truncateString(
                    `${window.location.origin}/storefront/${
                      value?.split("/storefront/")[1]
                    }`,
                    25,
                  )}{" "}
                </a>
                <CopyToClipboard
                  text={`${window.location.origin}/storefront/${
                    value?.split("/storefront/")[1]
                  }`}
                  onCopy={() => onCopy()}
                >
                  <img width="15px" className="mr-2" src={Icon.copy} alt="" />
                </CopyToClipboard>
              </>
            ) : row?.original?.template ? (
              <>
                <a
                  href={`${process.env.REACT_APP_LUMI_STOREFRONT_APP_BASE_URL}/glb?sfid=${row?.original?.id}`}
                  className="gray--text"
                  target="_blank"
                  rel="noreferrer"
                >
                  {truncateString(
                    `${process.env.REACT_APP_LUMI_STOREFRONT_APP_BASE_URL}/glb?sfid=${row?.original?.id}`,
                    25,
                  )}{" "}
                </a>
                <CopyToClipboard
                  text={`${process.env.REACT_APP_LUMI_STOREFRONT_APP_BASE_URL}/glb?sfid=${row?.original?.id}`}
                  onCopy={() => onCopy()}
                >
                  <img width="15px" className="mr-2" src={Icon.copy} alt="" />
                </CopyToClipboard>
              </>
            ) : (
              <>
                <a
                  href={`${window.location.origin}/storefront/${
                    value?.split("/storefront/")[1]
                  }`}
                  className="gray--text"
                  target="_blank"
                  rel="noreferrer"
                >
                  {truncateString(
                    `${window.location.origin}/storefront/${
                      value?.split("/storefront/")[1]
                    }`,
                    25,
                  )}{" "}
                </a>
                <CopyToClipboard
                  text={`${window.location.origin}/storefront/${
                    value?.split("/storefront/")[1]
                  }`}
                  onCopy={() => onCopy()}
                >
                  <span className="cursor-pointer">
                    <img width="15px" className="mr-2" src={Icon.copy} alt="" />
                  </span>
                </CopyToClipboard>
              </>
            )}
          </div>
        ),
      },
      {
        Header: "Action",
        accessor: "",
        Cell: ({ cell: { row } }) => {
          return (
            <span className="d-flex align-items-center" style={{ gap: "10px" }}>
              <secondaryComponents.NavigateWithPermission
                permissionParentKey={"Inventory Management"}
                permissionChildKey="can_view_edit_and_create_storefront"
              >
                <primaryComponents.Button
                  classNames={"btn btn--primary smallBtn"}
                  onClick={() =>
                    navigate(
                      pathConstants.INVENTORY_MENU_DETAILS({
                        menuId: row.original.id,
                      }),
                    )
                  }
                >
                  Update Inventory
                </primaryComponents.Button>

                <primaryComponents.Button
                  classNames={"btn btn--outline smallBtn"}
                  onClick={() =>
                    navigate(
                      pathConstants.INVENTORY_MENU_DETAILS_CUSTOMIZATION_CHECKOUT(
                        {
                          menuId: row.original.id,
                        },
                      ),
                    )
                  }
                >
                  Settings
                </primaryComponents.Button>

                <MoreActions
                  setIsDeleteFoodMenu={setIsDeleteFoodMenu}
                  setIsFoodMenuWidget={setIsFoodMenuWidget}
                  storefrontId={row?.original?.id}
                  isMobile={isMobile}
                />
              </secondaryComponents.NavigateWithPermission>
            </span>
          );
        },
      },
    ],
    [navigate],
  );

  const instance = useTable({ columns, data: storefrontList || [] });

  const { mutate: deleteStorfront, isLoading: isDeletingStorefront } =
    useDeleteStorefront({
      id: isDeleteFoodMenu,
    });
  const handleDeleteStorfront = () => {
    return deleteStorfront(
      {},
      {
        onSuccess: () => {
          setIsDeleteFoodMenu(null);
          triggerToast("Storefront deleted successfully", "success");
        },
      },
    );
  };

  return {
    isFoodMenuWidget,
    isDeleteFoodMenu,
    isDeletingStorefront,
    instance,
    setIsDeleteFoodMenu,
    setIsFoodMenuWidget,
    handleDeleteStorfront,
  };
};
